import { IGenericSearchFormattedAsset } from '@mais-retorno-front/core-business/useCases/search-asset/interface';
import { getRetornoAdss } from '@mais-retorno-front/graphQl/queries/featured-sheets';
import {
  formatCnpj,
  formatNumber,
} from '@mais-retorno-front/libs/util-helpers';
import { createContext, useContext, useEffect, useState } from 'react';
import { IRetornoAds } from './interface';
import { marketData, marketDataV4 } from '@core/market-data-sdk';
import { SearchItemEntity } from '@core/design-system/search';

interface IRetornoAdsContext {
  retornoAds: IRetornoAds[];
  featuredToSearch: IGenericSearchFormattedAsset | null;
}

export const RetornoAdsContext = createContext({
  retornoAds: [],
  featuredToSearch: null,
} as IRetornoAdsContext);

const getSponsoredAsset = async (cnpj: string) => {
  const assetList = await marketDataV4.general.search(cnpj);
  const asset = assetList?.find((item) => new SearchItemEntity(item).hasQuotes);

  return asset;
};

export const RetornoAdsProvider = ({ children }) => {
  const [retornoAds, setRetornoAds] = useState<IRetornoAds[] | null>([]);
  const [featuredToSearch, setFeaturedToSearch] =
    useState<IGenericSearchFormattedAsset>(null);

  const fetchSheetData = async () => {
    try {
      const { cnpjList, search_cnpj } = await getRetornoAdss();

      fetchRetornoAdSearch(search_cnpj || getShuffledItem(cnpjList));

      const sheetData = await Promise.all(
        cnpjList.map(async (cnpj) => {
          try {
            const asset = await getSponsoredAsset(cnpj);

            if (!asset) return null;

            const stats = await marketData.general.getStats({
              identifier: asset.identifier,
            });

            const data: IRetornoAds = {
              cnpj,
              slug: `/${asset.canonical_url}`,
              nicename: stats?.shortname,
              networth: asset?.additional_fields?.networth
                ? formatNumber(asset?.additional_fields?.networth, {
                    type: 'money',
                    thousandMask: true,
                  })
                : null,
              quota_holders: asset?.additional_fields?.quota_holders
                ? formatNumber(asset?.additional_fields?.quota_holders, {
                    thousandMask: true,
                    precision: 0,
                  })
                : null,
              volatility: formatNumber(
                stats?.stats?.timeframe?.begin?.volatility,
                {
                  type: 'percent',
                  thousandMask: true,
                }
              ),
              profitability: formatNumber(
                stats?.stats?.timeframe?.last_12_months?.profitability,
                { type: 'percent' }
              ),
              profitability_is_positive:
                stats?.stats?.timeframe?.last_12_months?.profitability >= 0,
            };

            return data;
          } catch (error) {
            return null;
          }
        })
      );

      setRetornoAds(sheetData.filter(Boolean));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchRetornoAdSearch = async (search_cnpj: string) => {
    try {
      const asset = await getSponsoredAsset(search_cnpj);

      if (!asset) return;

      const infos = [
        {
          displayName: 'Situação',
          slug: 'situacao',
          value: asset?.additional_fields?.situation,
        },
        {
          displayName: 'CNPJ',
          slug: 'cnpj',
          value: formatCnpj(asset?.additional_fields?.cnpj),
        },
      ];

      if (asset?.additional_fields?.networth) {
        infos.push({
          displayName: 'Patrimônio',
          slug: 'networth',
          value: formatNumber(asset?.additional_fields?.networth, {
            type: 'money',
            thousandMask: true,
          }),
        });
      }
      if (asset?.additional_fields?.quota_holders) {
        infos.push({
          displayName: 'Cotistas',
          slug: 'quota_holders',
          value: formatNumber(asset?.additional_fields?.quota_holders, {
            thousandMask: true,
          }),
        });
      }

      setFeaturedToSearch({
        cnpj: formatCnpj(asset?.additional_fields?.cnpj) as unknown as number,
        type: asset.type,
        slug: `/${asset.canonical_url}`,
        url: `/${asset.canonical_url}`,
        identifier: asset.identifier,
        nicename: asset.nicename,
        info: infos,
      });
    } catch (error) {}
  };

  useEffect(() => {
    fetchSheetData();
  }, []);

  return (
    <RetornoAdsContext.Provider value={{ retornoAds, featuredToSearch }}>
      {children}
    </RetornoAdsContext.Provider>
  );
};

export const useRetornoAds = () => {
  const context = useContext(RetornoAdsContext);

  if (context === undefined) {
    throw new Error('useRetornoAds must be used within a RetornoAdsProvider');
  }

  return context;
};

function getShuffledItem(items: any[]): string {
  const now = Date.now();
  const countItens = items.length;

  if (countItens === 1) {
    return items[0];
  }

  // se a hora atual for par, retorna o primeiro item. se for impar, retorna o segundo
  if (countItens === 2) {
    return items[now % 2 === 0 ? 0 : 1];
  }

  if (countItens > 3) {
    const randomIndex = Math.floor(Math.random() * countItens);
    return items[randomIndex];
  }
}
